// LeadDetailHeader — top bar of the Lead Detail page.
//   - Breadcrumb: Leads / {Name}
//   - "Use new design" toggle (default ON; OFF routes to the legacy inline
//     LeadDrawer view via the parent's onToggleDesign)
//   - GO BACK / PREV LEAD / NEXT LEAD navigation. PREV/NEXT resolve neighbours
//     from GET /leads/:id/siblings (tenant-scoped) using the persisted list
//     filter snapshot so navigation matches the agent's Leads view.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiDel } from '../../lib/api.js';

export default function LeadDetailHeader({ leadId, lead, useNewDesign, onToggleDesign, onBack, onNavigate }) {
  const name = lead ? [lead.firstName, lead.lastName].filter(Boolean).join(' ') || 'Lead' : 'Lead';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /** Delete this lead (backend enforces ownership + pool protection) and return to /leads. */
  async function deleteLead() {
    if (deleting) return;
    setDeleting(true);
    try {
      await apiDel(`/leads/${leadId}`);
      toast.success('Lead deleted');
      qc.removeQueries({ queryKey: ['lead', leadId] });
      qc.invalidateQueries({ queryKey: ['leads'] });
      navigate('/leads');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to delete lead');
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  // Reuse the last Leads-list filter snapshot for sibling ordering.
  let filter = '';
  try { filter = localStorage.getItem('leads:lastFilter') || ''; } catch { /* ignore */ }

  const { data } = useQuery({
    queryKey: ['lead-siblings', leadId, filter],
    queryFn: () => apiGet(`/leads/${leadId}/siblings${filter ? `?filter=${encodeURIComponent(filter)}` : ''}`),
    enabled: !!leadId,
  });
  const prevId = data?.prevId || null;
  const nextId = data?.nextId || null;

  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-civix-border bg-civix-panel">
      <button
        onClick={onBack}
        className="btn-ghost text-xs flex items-center gap-1.5"
        aria-label="Back to Leads"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Leads
      </button>

      <div className="text-xs text-white/40">
        <button onClick={onBack} className="hover:text-white/70 transition">Leads</button>
        <span className="mx-1">/</span>
        <span className="text-white/70">{name}</span>
      </div>

      <div className="ml-auto flex items-center gap-2">
        {/* Prev / Next */}
        <button
          onClick={() => prevId && onNavigate?.(prevId)}
          disabled={!prevId}
          aria-label="Previous lead"
          className="btn-ghost text-xs flex items-center gap-1 disabled:opacity-40"
        >
          <ChevronLeft className="w-4 h-4" /> Prev
        </button>
        <button
          onClick={() => nextId && onNavigate?.(nextId)}
          disabled={!nextId}
          aria-label="Next lead"
          className="btn-ghost text-xs flex items-center gap-1 disabled:opacity-40"
        >
          Next <ChevronRight className="w-4 h-4" />
        </button>

        {/* Use new design toggle */}
        <label className="flex items-center gap-1.5 text-[11px] text-white/60 cursor-pointer select-none ml-2">
          <input
            type="checkbox"
            checked={useNewDesign}
            onChange={(e) => onToggleDesign?.(e.target.checked)}
            className="accent-civix-purple"
          />
          Use new design
        </label>

        <button
          onClick={() => setConfirmDelete(true)}
          className="btn-ghost text-xs flex items-center gap-1 text-rose-400 hover:text-rose-300"
          aria-label="Delete lead"
        >
          <Trash2 className="w-4 h-4" /> Delete Lead
        </button>
      </div>

      {confirmDelete && (
        <div
          className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          data-testid="delete-lead-modal"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirmDelete(false); }}
        >
          <div className="panel p-6 w-full max-w-sm space-y-4">
            <h3 className="font-semibold text-lg">Delete lead?</h3>
            <p className="text-sm text-white/70">
              Are you sure you want to delete this lead ({lead?.firstName} {lead?.lastName})? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button
                type="button"
                className="btn-primary bg-rose-600 hover:bg-rose-500"
                disabled={deleting}
                onClick={deleteLead}
              >
                {deleting ? 'Deleting…' : 'Delete Lead'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
